import React, { createContext, useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import OrganismsBg from 'assets/organisms.svg'; // Default pattern background

// Darker gradient set for glassmorphism UI
export const THEME_GRADIENTS = [
  {
    name: 'Midnight Blue',
    value:
      'bg-[linear-gradient(to_bottom,_#1E3A8A,_#172554,_#0F172A)] dark:bg-[linear-gradient(to_bottom,_#0A0E23,_#0C132E,_#0E1A3A)]',
  },
  {
    name: 'Emerald Depths',
    value:
      'bg-[linear-gradient(135deg,_#064E3B,_#022C22,_#011B16)] dark:bg-[linear-gradient(135deg,_#01110C,_#011A13,_#012820)]',
  },
  {
    name: 'Burnt Ember',
    value:
      'bg-[linear-gradient(to_top_right,_#7C2D12,_#4B1D0E,_#1C0B07)] dark:bg-[linear-gradient(to_top_right,_#0B0403,_#130705,_#1A0B07)]',
  },
  {
    name: 'Violet Dusk',
    value:
      'bg-[linear-gradient(to_bottom,_#4C1D95,_#2E1065,_#1E093F)] dark:bg-[linear-gradient(to_bottom,_#0A0416,_#120A2C,_#1C103F)]',
  },
  {
    name: 'Crimson Veil',
    value:
      'bg-[linear-gradient(120deg,_#7F1D1D,_#4A0E0E,_#1E0606)] dark:bg-[linear-gradient(120deg,_#0A0202,_#120505,_#1A0808)]',
  },
  {
    name: 'Abyssal Teal',
    value:
      'bg-[linear-gradient(to_right,_#134E4A,_#083838,_#041F20)] dark:bg-[linear-gradient(to_right,_#010E11,_#02191B,_#03282A)]',
  },
  {
    name: 'Bronze Horizon',
    value:
      'bg-[linear-gradient(to_bottom_left,_#78350F,_#4A1C07,_#1F0C02)] dark:bg-[linear-gradient(to_bottom_left,_#0B0401,_#130805,_#1A0D09)]',
  },
  {
    name: 'Obsidian Steel',
    value:
      'bg-[linear-gradient(to_right,_#374151,_#1F2937,_#111827)] dark:bg-[linear-gradient(to_right,_#050709,_#0B0E12,_#0F141A)]',
  },
];

const DEFAULT_THEME = THEME_GRADIENTS[0].value;

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(Cookies.get('theme') || DEFAULT_THEME);
  const [background, setBackground] = useState(
    Cookies.get('bg') || OrganismsBg
  );
  const [darkMode, setDarkMode] = useState(
    Cookies.get('dark_mode') === 'true' || false
  );

  // Sync cookies when state changes
  useEffect(() => {
    Cookies.set('theme', theme, { expires: 365 });
    Cookies.set('bg', background, { expires: 365 });
    Cookies.set('dark_mode', darkMode, { expires: 365 });
  }, [theme, background, darkMode]);

  // Methods to modify theme easily
  const toggleDarkMode = useCallback(() => {
    setDarkMode((prev) => !prev);
  }, []);

  const changeTheme = useCallback((gradientValue) => {
    setTheme(gradientValue);
  }, []);

  const changeBackground = useCallback((bgSrc) => {
    setBackground(bgSrc);
  }, []);

  // Add ability to dynamically style nav items or borders with the background image
  const getThemedBorder = useCallback(
    (side = 'right', thickness = '4px') => {
      return {
        borderImageSource: `url(${background})`,
        borderImageSlice: 30,
        borderWidth: thickness,
        borderStyle: 'solid',
        borderColor: 'transparent',
        [`border${side.charAt(0).toUpperCase() + side.slice(1)}`]: thickness,
      };
    },
    [background]
  );

  return (
    <ThemeContext.Provider
      value={{
        theme,
        background,
        darkMode,
        toggleDarkMode,
        changeTheme,
        changeBackground,
        getThemedBorder,
        gradients: THEME_GRADIENTS,
      }}
    >
      <div className={`min-h-screen ${theme} transition-all duration-500`}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};
